// Deal Submission spec §10 — wire types for the System-Admin
// template authoring surface. Mirrors the backend DTOs in
// PhoenixCapital.Core/DTOs/DealSubmission/DealSubmissionDtos.cs.

import { api } from "./client";

export type DealSubmissionSectionKind = "DataEntry" | "Documents";

export interface DealSubmissionTemplateDto {
  id: string;
  name: string;
  description: string | null;
  active: boolean;
  version: number;
  brokerInstructions: string | null;
  sections: DealSubmissionSectionDto[];
  documentRequests: DealSubmissionDocumentRequestDto[];
  createdAt: string;
  modifiedAt: string | null;
}

export interface DealSubmissionSectionDto {
  id: string;
  order: number;
  title: string;
  kind: DealSubmissionSectionKind;
  instructions: string | null;
  required: boolean;
  visibleWhenJson: string | null;
  formSchemaJson: string | null;
  feedbackEnabled: boolean;
  assessmentEnabled: boolean;
}

export interface DealSubmissionDocumentRequestDto {
  id: string;
  sectionId: string;
  order: number;
  key: string;
  label: string;
  required: boolean;
  allowedTypes: string | null;
  documentTagCode: string | null;
  destinationRule: string | null;
  instructions: string | null;
  visibleWhenJson: string | null;
  feedbackEnabled: boolean;
  assessmentEnabled: boolean;
}

export interface TemplateSectionInput {
  id: string | null;
  order: number;
  title: string;
  kind: DealSubmissionSectionKind;
  instructions: string | null;
  required: boolean;
  visibleWhenJson: string | null;
  formSchemaJson: string | null;
  feedbackEnabled: boolean;
  assessmentEnabled: boolean;
}

export interface TemplateDocumentRequestInput {
  id: string | null;
  sectionId: string | null;
  sectionOrder: number | null;
  order: number;
  key: string;
  label: string;
  required: boolean;
  allowedTypes: string | null;
  documentTagCode: string | null;
  destinationRule: string | null;
  instructions: string | null;
  visibleWhenJson: string | null;
  // AI evaluation — per-document AI toggles (default true server-side).
  feedbackEnabled?: boolean;
  assessmentEnabled?: boolean;
}

export interface CreateDealSubmissionTemplatePayload {
  name: string;
  description: string | null;
  brokerInstructions: string | null;
  sections: TemplateSectionInput[];
  documentRequests: TemplateDocumentRequestInput[];
}

export type UpdateDealSubmissionTemplatePayload =
  CreateDealSubmissionTemplatePayload;

// Per-section AI prompts — mirrors the Collection prompt CRUD but scoped to
// a section. Two rows per section (Feedback + Assessment); saving bumps the
// version and repoints the section FK. Only editable on a saved section
// (the parent admin UI hides the editor on unsaved/new sections).
export type DealSubmissionPromptKind = "Feedback" | "Assessment";
export type DealSubmissionPromptOutputLength = "Short" | "Medium" | "Long";

export interface DealSubmissionPromptDto {
  id: string;
  sectionId: string | null;
  documentRequestId?: string | null;
  kind: DealSubmissionPromptKind;
  body: string;
  outputLength?: DealSubmissionPromptOutputLength | null;
  version: number;
  createdAt: string;
  modifiedAt: string;
  modifiedByUserId: string | null;
}

export const dealSubmissionPromptsApi = {
  list: (templateId: string, sectionId: string) =>
    api.get<DealSubmissionPromptDto[]>(
      `/deal-submission/templates/${templateId}/sections/${sectionId}/prompts`,
    ),
  get: (templateId: string, sectionId: string, kind: DealSubmissionPromptKind) =>
    api.get<DealSubmissionPromptDto>(
      `/deal-submission/templates/${templateId}/sections/${sectionId}/prompts/${kind}`,
    ),
  save: (
    templateId: string,
    sectionId: string,
    kind: DealSubmissionPromptKind,
    body: string,
    outputLength: DealSubmissionPromptOutputLength = "Medium",
  ) =>
    api.put<DealSubmissionPromptDto>(
      `/deal-submission/templates/${templateId}/sections/${sectionId}/prompts/${kind}`,
      { body, outputLength },
    ),
};

// AI evaluation — per-document prompts. Mirrors dealSubmissionPromptsApi but
// scoped under a saved document-request id. Same kinds (Feedback / Assessment),
// same versioning behavior.
export const dealSubmissionDocumentPromptsApi = {
  list: (templateId: string, documentRequestId: string) =>
    api.get<DealSubmissionPromptDto[]>(
      `/deal-submission/templates/${templateId}/document-requests/${documentRequestId}/prompts`,
    ),
  get: (
    templateId: string,
    documentRequestId: string,
    kind: DealSubmissionPromptKind,
  ) =>
    api.get<DealSubmissionPromptDto>(
      `/deal-submission/templates/${templateId}/document-requests/${documentRequestId}/prompts/${kind}`,
    ),
  save: (
    templateId: string,
    documentRequestId: string,
    kind: DealSubmissionPromptKind,
    body: string,
    outputLength: DealSubmissionPromptOutputLength = "Medium",
  ) =>
    api.put<DealSubmissionPromptDto>(
      `/deal-submission/templates/${templateId}/document-requests/${documentRequestId}/prompts/${kind}`,
      { body, outputLength },
    ),
};

export const dealSubmissionTemplatesApi = {
  list: (includeInactive = false) =>
    api.get<DealSubmissionTemplateDto[]>("/deal-submission/templates", {
      includeInactive,
    }),
  get: (id: string) =>
    api.get<DealSubmissionTemplateDto>(`/deal-submission/templates/${id}`),
  create: (body: CreateDealSubmissionTemplatePayload) =>
    api.post<DealSubmissionTemplateDto>("/deal-submission/templates", body),
  update: (id: string, body: UpdateDealSubmissionTemplatePayload) =>
    api.put<DealSubmissionTemplateDto>(
      `/deal-submission/templates/${id}`,
      body,
    ),
  deactivate: (id: string) =>
    api.post<null>(`/deal-submission/templates/${id}/deactivate`),
  // The inverse of deactivate. Deactivating the last active template of an
  // origin takes broker deal submission (503) and buyer escalation offline, and
  // an inactive template cannot be edited — so without this there was no way
  // back through the app.
  activate: (id: string) =>
    api.post<null>(`/deal-submission/templates/${id}/activate`),
};
